import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuthStore } from '../store/authStore';
import { Loader2 } from 'lucide-react';
import { UrawaIcon } from '../components/UrawaIcon';

export function RegisterPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const { register, googleLogin, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    try {
      await register({ email, password, full_name: fullName });
      navigate('/app');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    }
  };

  return (
    <div className="min-h-screen bg-legal-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <UrawaIcon className="h-12 w-12 text-accent" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-legal-900">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-legal-600">
          Start researching Indian law in minutes
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-legal-700">
                Full Name
              </label>
              <input
                id="full_name"
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-legal-300 rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-legal-700">
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-legal-300 rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-legal-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-legal-300 rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent"
              />
              <p className="mt-1 text-xs text-legal-500">Minimum 8 characters</p>
            </div>

            <div>
              <label htmlFor="confirm_password" className="block text-sm font-medium text-legal-700">
                Confirm Password
              </label>
              <input
                id="confirm_password"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-legal-300 rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-legal-900 bg-accent hover:bg-accent-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Create account'}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-legal-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-legal-500">Or sign up with</span>
              </div>
            </div>
            <div className="mt-4 flex justify-center">
              <GoogleLogin
                onSuccess={async (response) => {
                  if (response.credential) {
                    try {
                      await googleLogin(response.credential);
                      navigate('/app');
                    } catch {
                      setError('Google sign-up failed. Please try again.');
                    }
                  }
                }}
                onError={() => setError('Google sign-up failed. Please try again.')}
                theme="outline"
                size="large"
                width="100%"
              />
            </div>
          </div>

          <p className="mt-4 text-center text-sm text-legal-600">
            Already have an account?{' '}
            <Link to="/login" className="text-accent hover:text-accent-600">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
